import { useMemo, useState } from 'react';
import type { PromptTemplate } from '@cm/protocol';
import { extractVariables, renderPrompt } from '@cm/protocol';
import { api } from '../api';
import { paneName } from '../layout';
import { useActiveLayout, useStore } from '../store';

export function PromptPanel() {
  const prompts = useStore((s) => s.prompts);
  // Os alvos do broadcast aparecem pelo apelido que o usuario deu ao pane;
  // mostrar o title do daemon aqui e o apelido no pane confundiria a conferencia
  // antes de disparar um prompt para varias sessoes.
  const layout = useActiveLayout();
  const sessions = useStore((s) => s.sessions);
  const selectedIds = useStore((s) => s.selectedIds);
  const focusedId = useStore((s) => s.focusedId);
  const pushToast = useStore((s) => s.pushToast);
  const refreshPrompts = useStore((s) => s.refreshPrompts);
  const openPanel = useStore((s) => s.openPanel);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [vars, setVars] = useState<Record<string, string>>({});
  const [freeText, setFreeText] = useState('');
  const [submitOnSend, setSubmitOnSend] = useState(true);
  const [editing, setEditing] = useState<PromptTemplate | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Alvos do envio: as sessoes marcadas na sidebar; se nenhuma estiver
   * marcada, cai na sessao em foco. Assim o botao nunca fica sem destino.
   */
  const targets = useMemo(() => {
    const ids = selectedIds.length ? selectedIds : focusedId ? [focusedId] : [];
    return sessions.filter((s) => ids.includes(s.id) && s.status === 'running');
  }, [selectedIds, focusedId, sessions]);

  const active = prompts.find((p) => p.id === activeId) ?? null;
  const body = active?.body ?? freeText;
  const variables = useMemo(() => extractVariables(body), [body]);
  const preview = useMemo(() => renderPrompt(body, vars), [body, vars]);

  async function send() {
    if (!targets.length) {
      pushToast({ kind: 'warn', text: 'marque ao menos uma sessão ativa para receber o prompt' });
      return;
    }
    setBusy(true);
    try {
      const result = await api.sendPrompt({
        sessionIds: targets.map((t) => t.id),
        promptId: active?.id,
        text: active ? undefined : freeText,
        vars,
        submit: submitOnSend,
      });
      if (result.delivered.length) {
        pushToast({
          kind: 'success',
          text: `enviado para ${result.delivered.length} sessão(ões)`,
        });
      }
      for (const fail of result.failed) {
        pushToast({ kind: 'error', text: `${shortId(fail.sessionId)}: ${fail.reason}` });
      }
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    if (!editing) return;
    setBusy(true);
    try {
      if (editing.id === 'novo') {
        await api.createPrompt({
          name: editing.name,
          body: editing.body,
          description: editing.description,
          tags: editing.tags,
        });
      } else {
        await api.updatePrompt(editing.id, {
          name: editing.name,
          body: editing.body,
          description: editing.description,
          tags: editing.tags,
        });
      }
      await refreshPrompts();
      setEditing(null);
      pushToast({ kind: 'success', text: 'prompt salvo' });
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function remove(prompt: PromptTemplate) {
    if (!window.confirm(`Excluir o prompt "${prompt.name}"?`)) return;
    try {
      await api.deletePrompt(prompt.id);
      if (activeId === prompt.id) setActiveId(null);
      await refreshPrompts();
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    }
  }

  if (editing) {
    return (
      <div className="panel">
        <div className="panel-head">
          <h2>{editing.id === 'novo' ? 'Novo prompt' : 'Editar prompt'}</h2>
          <button className="ghost" onClick={() => setEditing(null)}>
            ✕
          </button>
        </div>

        <div className="field">
          <label>Nome</label>
          <input
            value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Descrição</label>
          <input
            value={editing.description ?? ''}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Tags (separadas por vírgula)</label>
          <input
            value={editing.tags.join(', ')}
            onChange={(e) =>
              setEditing({
                ...editing,
                tags: e.target.value
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
        <div className="field">
          <label>Corpo — use {'{{variavel}}'} para campos preenchíveis</label>
          <textarea
            value={editing.body}
            style={{ minHeight: 200 }}
            onChange={(e) => setEditing({ ...editing, body: e.target.value })}
          />
        </div>

        {extractVariables(editing.body).length > 0 && (
          <p className="card-desc">
            Variáveis detectadas: {extractVariables(editing.body).join(', ')}
          </p>
        )}

        <div className="row end">
          <button onClick={() => setEditing(null)}>Cancelar</button>
          <button className="primary" onClick={() => void saveEdit()} disabled={busy || !editing.name}>
            Salvar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Prompts</h2>
        <button
          className="ghost"
          title="Novo prompt"
          onClick={() =>
            setEditing({
              id: 'novo',
              name: '',
              body: '',
              description: '',
              tags: [],
              variables: [],
              createdAt: '',
              updatedAt: '',
            })
          }
        >
          +
        </button>
        <button className="ghost" onClick={() => openPanel(null)}>
          ✕
        </button>
      </div>

      <div className="broadcast-target">
        {targets.length === 0 ? (
          <span style={{ color: 'var(--warn)' }}>
            nenhuma sessão ativa selecionada — marque na barra lateral
          </span>
        ) : (
          <>
            enviar para <strong>{targets.length}</strong> sessão(ões):{' '}
            {targets.map((t) => paneName(layout, t.id, t.title)).join(', ')}
          </>
        )}
      </div>

      <div className="field">
        <label>Texto livre</label>
        <textarea
          value={freeText}
          onChange={(e) => {
            setFreeText(e.target.value);
            setActiveId(null);
          }}
          placeholder="Digite aqui ou escolha um prompt salvo abaixo."
        />
      </div>

      {variables.length > 0 && (
        <>
          <label>Variáveis</label>
          {variables.map((name) => (
            <div className="field" key={name}>
              <input
                placeholder={name}
                value={vars[name] ?? ''}
                onChange={(e) => setVars({ ...vars, [name]: e.target.value })}
              />
            </div>
          ))}
        </>
      )}

      {body && variables.length > 0 && (
        <div className="argv-preview" style={{ whiteSpace: 'pre-wrap' }}>
          {preview}
        </div>
      )}

      <div className="checkbox-row">
        <input
          id="submit-on-send"
          type="checkbox"
          checked={submitOnSend}
          onChange={(e) => setSubmitOnSend(e.target.checked)}
        />
        <label htmlFor="submit-on-send">Enviar Enter no final (submete o prompt)</label>
      </div>

      <div className="row" style={{ marginBottom: 18 }}>
        <button
          className="primary grow"
          onClick={() => void send()}
          disabled={busy || !body.trim() || targets.length === 0}
        >
          {targets.length > 1 ? `Broadcast para ${targets.length} panes` : 'Enviar'}
        </button>
      </div>

      <h2 style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--text-faint)' }}>
        Biblioteca
      </h2>

      {prompts.map((prompt) => (
        <div
          className="card"
          key={prompt.id}
          style={activeId === prompt.id ? { borderColor: 'var(--accent)' } : undefined}
        >
          <div className="card-title">{prompt.name}</div>
          {prompt.description && <div className="card-desc">{prompt.description}</div>}
          {prompt.tags.length > 0 && (
            <div className="tag-row">
              {prompt.tags.map((tag) => (
                <span className="tag" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="row">
            <button
              onClick={() => {
                setActiveId(prompt.id);
                setFreeText('');
                setVars({});
              }}
            >
              usar
            </button>
            <button className="ghost" onClick={() => setEditing(prompt)}>
              editar
            </button>
            <button className="ghost danger" onClick={() => void remove(prompt)}>
              excluir
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function shortId(id: string): string {
  return id.slice(0, 8);
}
